import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  FormControl,
  Tooltip,
  Chip,
  FormLabel,
  TextField,
  Stack,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from 'chart.js';
import { fetchProductivityData } from '../api';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

const ProductivityDashboard = () => {
  const [productivityData, setProductivityData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('week');
  const [dateRangeType, setDateRangeType] = useState('week');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);

  // Fetch productivity data when component mounts or dateRange changes
  const loadProductivityData = useCallback(async () => {
    try {
      setLoading(true);
      let data = await fetchProductivityData(dateRange);
      
      // Filter out user ID 1 (Sanduke)
      data = data.filter(employee => employee.id !== 1);
      
      // Process the data to include calculated fields
      const processedData = data.map(employee => ({
        ...employee,
        // Calculate pages per hour, ensuring we don't divide by zero
        pagesPerHour: employee.hoursWorked > 0 
          ? employee.totalPages / employee.hoursWorked 
          : 0,
        // Calculate efficiency score (pages per hour as a percentage of 200)
        efficiencyScore: Math.min(100, (employee.totalPages / (employee.hoursWorked || 1) / 2)),
        // Format hours worked to 1 decimal place
        formattedHours: employee.hoursWorked.toFixed(1)
      }));
      
      setProductivityData(processedData);
      setError(null);
    } catch (err) {
      console.error('Error loading productivity data:', err);
      setError('Failed to load productivity data. Please try again later.');
      // Keep the existing data if available to prevent UI flicker
      if (productivityData.length === 0) {
        setProductivityData([]);
      }
    } finally {
      setLoading(false);
    }
  }, [dateRange, productivityData.length]);

  // Load data when component mounts or dateRange changes
  useEffect(() => {
    loadProductivityData();
  }, [loadProductivityData]);

  // Calculate metrics
  const totalPages = productivityData.reduce((sum, emp) => sum + (emp.totalPages || 0), 0);
  const totalHours = productivityData.reduce((sum, emp) => sum + (emp.hoursWorked || 0), 0);
  const avgPagesPerHr = totalHours > 0 ? totalPages / totalHours : 0;
  
  // Calculate consistency (standard deviation of daily page counts)
  const calculateConsistency = (employee) => {
    if (!employee.uploadSessions || employee.uploadSessions.length === 0) return 0;
    
    // Only use weekday sessions
    const weekdaySessions = employee.uploadSessions.filter(session => {
      if (!session.timestamp) return false;
      const day = new Date(session.timestamp).getDay();
      return day !== 0 && day !== 6;
    });
    if (weekdaySessions.length === 0) return 0;
    
    const pageCounts = weekdaySessions.map(session => session.pageCount || 0);
    const avg = pageCounts.reduce((a, b) => a + b, 0) / pageCounts.length;
    const squareDiffs = pageCounts.map(value => Math.pow(value - avg, 2));
    const avgSquareDiff = squareDiffs.reduce((a, b) => a + b, 0) / squareDiffs.length;
    const stdDev = Math.sqrt(avgSquareDiff);
    
    // Convert to a 0-100 consistency score (lower std dev = higher consistency)
    return Math.max(0, 100 - (stdDev * 10));
  };

  // Calculate date range based on selection
  const getDateRange = () => {
    const now = new Date();
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    
    switch(dateRangeType) {
      case 'today':
        return { startDate: new Date(start), endDate: new Date(now) };
      case 'yesterday':
        start.setDate(start.getDate() - 1);
        const yesterdayEnd = new Date(start);
        yesterdayEnd.setHours(23, 59, 59, 999);
        return { startDate: new Date(start), endDate: yesterdayEnd };
      case 'week':
        start.setDate(start.getDate() - 7);
        return { startDate: new Date(start), endDate: new Date(now) };
      case 'lastWeek':
        start.setDate(start.getDate() - 14);
        const lastWeekEnd = new Date(start);
        lastWeekEnd.setDate(lastWeekEnd.getDate() + 7);
        lastWeekEnd.setHours(23, 59, 59, 999);
        return { startDate: new Date(start), endDate: lastWeekEnd };
      case 'month':
        start.setMonth(start.getMonth() - 1);
        return { startDate: new Date(start), endDate: new Date(now) };
      case 'threeMonths':
        start.setMonth(start.getMonth() - 3);
        return { startDate: new Date(start), endDate: new Date(now) };
      case 'lastMonth':
        const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
        lastMonthEnd.setHours(23, 59, 59, 999);
        return { startDate: lastMonthStart, endDate: lastMonthEnd };
      case 'year':
        start.setFullYear(start.getFullYear() - 1);
        return { startDate: new Date(start), endDate: new Date(now) };
      case 'custom':
        return { 
          startDate: customStartDate || new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000), 
          endDate: customEndDate || new Date(now) 
        };
      default:
        start.setDate(start.getDate() - 7);
        return { startDate: new Date(start), endDate: new Date(now) };
    }
  };

  // Process data for charts with proper date filtering
  const processDailyData = () => {
    const { startDate, endDate } = getDateRange();
    
    // Generate all weekdays in the range for consistent x-axis
    const dateLabels = [];
    const dateMap = new Map();
    const currentDate = new Date(startDate);
    
    while (currentDate <= endDate) {
      const day = currentDate.getDay(); // 0 = Sun, 6 = Sat
      if (day !== 0 && day !== 6) { // Only Mon-Fri
        const dateKey = currentDate.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric',
          weekday: 'short' 
        });
        dateLabels.push(dateKey);
        dateMap.set(dateKey, 0);
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // Process employee data, only count weekday sessions
    productivityData.forEach(employee => {
      if (!employee.uploadSessions) return;
      
      employee.uploadSessions.forEach(session => {
        if (!session.timestamp) return;
        
        const sessionDate = new Date(session.timestamp);
        if (sessionDate < startDate || sessionDate > endDate) return;
        
        const day = sessionDate.getDay();
        if (day === 0 || day === 6) return; // Skip weekends
        
        const dateKey = sessionDate.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric',
          weekday: 'short' 
        });
        
        const pages = session.pageCount || 0;
        dateMap.set(dateKey, (dateMap.get(dateKey) || 0) + pages);
      });
    });
    
    // Convert to arrays for chart
    const data = Array.from(dateMap.values());
    
    return { 
      labels: dateLabels, 
      data,
      dateRange: { startDate, endDate }
    };
  };

  // Get date range and process data
  const { startDate, endDate } = getDateRange();
  const { 
    labels: dailyLabels, 
    data: dailyData
  } = processDailyData();
  
  // Format date range for display
  const formatDateRange = () => {
    if (!startDate || !endDate) return '';
    
    const formatOptions = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    };
    
    return `${startDate.toLocaleDateString('en-US', formatOptions)} - ${endDate.toLocaleDateString('en-US', formatOptions)}`;
  };
  
  // Prepare chart data
  const lineDataDailyTotals = {
    labels: dailyLabels.length > 0 ? dailyLabels : ['No data'],
    datasets: [
      {
        label: 'Total Pages Scanned',
        data: dailyData.length > 0 ? dailyData : [0],
        fill: true,
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderColor: 'rgba(75, 192, 192, 1)',
        tension: 0.1
      }
    ]
  };

  const barDataPagesPerHour = {
    labels: productivityData.length > 0 
      ? productivityData.map(emp => emp.name)
      : ['No data'],
    datasets: [{
      label: 'Pages/Hour',
      data: productivityData.length > 0 
        ? productivityData.map(emp => emp.pagesPerHour || 0)
        : [0],
      backgroundColor: 'rgba(54, 162, 235, 0.6)'
    }]
  };

  // Prepare data for the employee contributions chart
  const employeeContributionData = {
    labels: dailyLabels.length > 0 ? dailyLabels : ['No data'],
    datasets: productivityData.map((emp, idx) => ({
      label: emp.name,
      data: dailyLabels.map(date => {
        if (!emp.uploadSessions) return 0;
        const session = emp.uploadSessions.find(s => 
          new Date(s.timestamp).toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            weekday: 'short' 
          }) === date
        );
        return session ? (session.pageCount || 0) : 0;
      }),
      borderColor: `hsl(${(idx * 137.5) % 360}, 70%, 50%)`,
      backgroundColor: `hsla(${(idx * 137.5) % 360}, 70%, 50%, 0.1)`,
      tension: 0.3,
      fill: true
    }))
  };

  // KPI Cards
  const renderKPICards = () => {
    // Get weekday count from processDailyData
    const { labels: weekdayLabels } = processDailyData();
    const avgPagesPerDay = (weekdayLabels.length > 0)
      ? (totalPages / weekdayLabels.length)
      : 0;

    return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Avg. Pages/Day</Typography>
              <Typography variant="h6">
                {loading ? <CircularProgress size={20} /> : avgPagesPerDay.toFixed(1)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Employees Tracked</Typography>
              <Typography variant="h6">
                {loading ? <CircularProgress size={20} /> : productivityData.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Pages</Typography>
              <Typography variant="h6">
                {loading ? <CircularProgress size={20} /> : totalPages.toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card variant="outlined">
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Avg. Pages/Hr</Typography>
              <Typography variant="h6">
                {loading ? <CircularProgress size={20} /> : avgPagesPerHr.toFixed(1)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return <Typography color="error">{error}</Typography>;
    }

    return (
      <Grid container spacing={4}>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>Daily Pages Scanned</Typography>
            <Line 
              data={lineDataDailyTotals} 
              options={{ 
                responsive: true, 
                plugins: { 
                  legend: { display: false },
                  tooltip: {
                    callbacks: {
                      label: (context) => `${context.parsed.y} pages`
                    }
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    title: {
                      display: true,
                      text: 'Pages Scanned'
                    }
                  }
                }
              }} 
              height={120} 
            />
          </Paper>
          
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>Employee Contributions (Daily)</Typography>
            <Line 
              data={employeeContributionData} 
              options={{ 
                responsive: true, 
                plugins: { 
                  legend: { 
                    position: 'bottom',
                    labels: {
                      boxWidth: 12
                    }
                  },
                  tooltip: {
                    callbacks: {
                      label: (context) => `${context.dataset.label}: ${context.parsed.y} pages`
                    }
                  }
                },
                interaction: {
                  mode: 'index',
                  intersect: false
                },
                scales: {
                  y: {
                    stacked: true,
                    beginAtZero: true,
                    title: {
                      display: true,
                      text: 'Pages Scanned'
                    }
                  }
                }
              }} 
              height={200}
            />
          </Paper>
        </Grid>
        
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>Pages per Hour by Employee</Typography>
            <Bar 
              data={barDataPagesPerHour} 
              options={{ 
                responsive: true, 
                indexAxis: 'y',
                plugins: { 
                  legend: { display: false },
                  tooltip: {
                    callbacks: {
                      label: (context) => `${context.parsed.x} pages/hour`
                    }
                  }
                },
                scales: {
                  x: {
                    beginAtZero: true,
                    title: {
                      display: true,
                      text: 'Pages per Hour'
                    }
                  }
                }
              }} 
              height={Math.max(200, productivityData.length * 40)}
            />
          </Paper>
          
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Performance Summary</Typography>
            {productivityData.length > 0 ? (
              <Box>
                {productivityData.map((emp, idx) => (
                  <Box key={emp.id} sx={{ 
                    mb: 2, 
                    p: 1, 
                    bgcolor: idx % 2 === 0 ? 'action.hover' : 'background.paper', 
                    borderRadius: 1 
                  }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="subtitle2">{emp.name}</Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Tooltip title={`${emp.pagesPerHour.toFixed(1)} pages/hour`}>
                          <Chip 
                            size="small"
                            label={`${emp.pagesPerHour.toFixed(1)}/hr`}
                            color={emp.pagesPerHour > 100 ? 'success' : emp.pagesPerHour > 50 ? 'warning' : 'error'}
                            variant="outlined"
                          />
                        </Tooltip>
                        <Tooltip title={`${emp.totalPages} total pages`}>
                          <Chip 
                            size="small"
                            label={`${emp.totalPages} pages`}
                            variant="outlined"
                          />
                        </Tooltip>
                      </Box>
                    </Box>
                    <Box display="flex" justifyContent="space-between" mt={0.5}>
                      <Typography variant="caption" color="textSecondary">
                        {emp.uploadSessions?.length || 0} sessions
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {emp.formattedHours} hours
                      </Typography>
                      <Tooltip title={`Consistency: ${calculateConsistency(emp).toFixed(0)}%`}>
                        <Typography variant="caption" color="textSecondary">
                          {calculateConsistency(emp).toFixed(0)}% consistent
                        </Typography>
                      </Tooltip>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 2 }}>
                No productivity data available for the selected period
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ width: '100%' }}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
            <Box>
              <Typography variant="h5" component="h2" gutterBottom>
                Scanning Productivity Dashboard
              </Typography>
              <Typography variant="subtitle2" color="textSecondary">
                {formatDateRange()}
              </Typography>
            </Box>
            
            <Box sx={{ maxWidth: 800 }}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" sx={{ fontSize: '0.75rem', mb: 1 }}>Time Period</FormLabel>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <ToggleButtonGroup
                    value={dateRangeType}
                    exclusive
                    onChange={(e, newValue) => {
                      if (newValue !== null) {
                        setDateRangeType(newValue);
                        if (newValue !== 'custom') {
                          setDateRange(newValue);
                        }
                      }
                    }}
                    size="small"
                    aria-label="time period"
                  >
                    <ToggleButton value="today">Today</ToggleButton>
                    <ToggleButton value="yesterday">Yesterday</ToggleButton>
                    <ToggleButton value="week">Week</ToggleButton>
                    <ToggleButton value="month">Month</ToggleButton>
                    <ToggleButton value="threeMonths">3 Months</ToggleButton>
                    <ToggleButton value="year">Year</ToggleButton>
                    <ToggleButton value="custom">Custom</ToggleButton>
                  </ToggleButtonGroup>
                  
                  {dateRangeType === 'custom' && (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ ml: 1 }}>
                      <DatePicker
                        label="Start Date"
                        value={customStartDate}
                        onChange={(newValue) => {
                          setCustomStartDate(newValue);
                          if (newValue) {
                            setDateRange('custom');
                          }
                        }}
                        renderInput={(params) => (
                          <TextField 
                            {...params} 
                            size="small" 
                            sx={{ width: 150 }} 
                            inputProps={{
                              ...params.inputProps,
                              placeholder: 'Start Date'
                            }}
                          />
                        )}
                      />
                      <Typography>to</Typography>
                      <DatePicker
                        label="End Date"
                        value={customEndDate}
                        onChange={(newValue) => {
                          setCustomEndDate(newValue);
                          if (newValue) {
                            setDateRange('custom');
                          }
                        }}
                        renderInput={(params) => (
                          <TextField 
                            {...params} 
                            size="small" 
                            sx={{ width: 150 }} 
                            inputProps={{
                              ...params.inputProps,
                              placeholder: 'End Date'
                            }}
                          />
                        )}
                      />
                    </Stack>
                  )}
                </Stack>
              </FormControl>
            </Box>
          </Box>
          
          {renderKPICards()}
          {renderContent()}
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default ProductivityDashboard;
